import { useModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { useToast } from "@src/appV2/lib";
import { APP_V2_APP_EVENTS, APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { getAddressByGeoCode } from "@src/appV2/lib/GoogleMaps/googleMaps";
import { convertToGoogleMapsLocation } from "@src/appV2/Location";
import { enableGeoLocation, isGeoLocationEnabled } from "@src/appV2/Location/deviceLocation/enable";
import { getDeviceGeoLocation } from "@src/appV2/Location/deviceLocation/geoLocation";
import { type Place } from "@src/appV2/redesign/Onboarding/OnboardingFlowContainer/AddressStep/types";
import { useGetWorker } from "@src/appV2/Worker/api/useGetWorker";
import { isEmpty } from "lodash";
import { useEffect, useState } from "react";

interface UseWorkerAddressProps {
  shouldShowEditAddress?: boolean;
}

export function useWorkerAddress(props: UseWorkerAddressProps) {
  const { shouldShowEditAddress = false } = props;
  const addressInputModalState = useModalState(shouldShowEditAddress ? "open" : "closed");
  const { showErrorToast } = useToast();

  const [loadingData, setLoadingData] = useState(true);
  const [place, setPlace] = useState<Place | Partial<Place>>({});
  const [addressInput, setAddressInput] = useState("");
  const [addressInputErrorMessage, setAddressInputErrorMessage] = useState("");
  const [isEnablingGeoLocation, setIsEnablingGeoLocation] = useState(false);

  const { data: worker } = useGetWorker({ enabled: false });

  useEffect(() => {
    if (!isDefined(worker)) {
      return;
    }
    // important. we need to setLoadingData true here
    // in case the user navigates back and forth through the screens

    // loadingData forces a re-render of the LoadingEdit component
    // when our state in this component changes
    setLoadingData(true);
    const { geoLocation, address } = worker;
    const lat = geoLocation?.coordinates?.[1];
    const lng = geoLocation?.coordinates?.[0];
    if (!isEmpty(address)) {
      setPlace({
        address,
        location: lat && lng ? { lat, lng } : undefined,
      });
      setAddressInput(address?.formatted ?? "");
    }

    setLoadingData(false);
  }, [worker]);

  function validatePlace(newPlace: Place | Partial<Place>) {
    const { address: { streetName, streetNumber, city, state, country, postalCode } = {} } =
      newPlace;

    if (streetName && streetNumber && city && state && country && postalCode) {
      setAddressInputErrorMessage("");
    } else {
      logEvent(APP_V2_APP_EVENTS.GET_ADDRESS_INCOMPLETE_DATA_FAILURE, {
        changedPlace: newPlace,
      });
      setAddressInputErrorMessage(
        "That doesn't look like a complete address. Make sure to enter the full address of your current residence."
      );
    }
  }

  async function handleUseCurrentLocation() {
    try {
      setIsEnablingGeoLocation(true);
      let locationEnabled = false;
      const geoLocationEnabled = await isGeoLocationEnabled();

      logEvent(APP_V2_USER_EVENTS.ONBOARDING_HOME_ADDRESS_CHECK_LOCATION_PERMISSION, {
        locationEnabled,
      });

      locationEnabled = geoLocationEnabled;

      if (!geoLocationEnabled) {
        const { success } = await enableGeoLocation();
        logEvent(APP_V2_USER_EVENTS.ONBOARDING_HOME_ADDRESS_ASK_LOCATION_PERMISSION, {
          success,
        });
        locationEnabled = success;
      }

      if (locationEnabled) {
        const { geoLocation } = await getDeviceGeoLocation();
        const googleMapsLocation = convertToGoogleMapsLocation(geoLocation);
        const address = await getAddressByGeoCode(googleMapsLocation);

        if (address?.formatted) {
          validatePlace({
            address,
            location: googleMapsLocation,
          });
          setPlace({
            address,
            location: googleMapsLocation,
          });
          setAddressInput(address?.formatted ?? "");
        } else {
          showErrorToast("Could not get current location, try entering your address");
        }
      } else {
        showErrorToast("Please allow Geo Location access");
      }
    } catch {
      showErrorToast("Please allow Geo Location access");
    } finally {
      // We always want to open the modal to let the user enter the address manually
      addressInputModalState.openModal();
      setIsEnablingGeoLocation(false);
    }
  }

  function handleManualEntry() {
    addressInputModalState.openModal();
  }

  function handlePlaceChange(newPlace: Place | Partial<Place>) {
    const { address } = newPlace;

    validatePlace(newPlace);

    const updatedPlace = {
      ...newPlace,
      address: {
        ...address,
        line1: address?.line1 ?? place?.address?.line1 ?? "",
      },
    };

    setPlace(updatedPlace);
    setAddressInput(updatedPlace.address?.formatted ?? "");
  }

  const handleAddressInputChange = (value: string) => {
    setAddressInput(value);
    setPlace({});
  };

  return {
    place,
    loadingData,
    handleUseCurrentLocation,
    addressInputModalState,
    addressInput,
    handleAddressInputChange,
    handlePlaceChange,
    handleManualEntry,
    addressInputErrorMessage,
    isEnablingGeoLocation,
  };
}
